'use client'

/* The contents rail. Behaviour is ported from the artboard's data-dc-script
   (Article Reader.dc.html :326-409) rather than from build_post_html.py, whose rail JS is
   a design generation behind.

   Two things worth knowing before editing:

   - `data-on` sits on the active anchor, not on <nav>. The artboard put it on <nav> and
     generated one CSS rule per heading id, because the Claude Design runtime owns `class`
     and wipes imperative writes on every re-render. React owns the attribute here, so one
     rule and one attribute do the same job.
   - `data-passed` is derived from position, not stored. The artboard sweeps every anchor
     at or above the current one; the toc is already in document order, so index <= active
     index is the same set. */

import { useEffect, useMemo, useRef, useState } from 'react'
import type React from 'react'
import type { TocEntry } from '@/lib/articles/markdown'
import { gaEvent } from '@/lib/gtag'

const COMPACT = '(max-width:1400px)'
/** The artboard picks the last heading whose top is above scrollY + 140. */
const SPY_OFFSET = 140
/** Margin kept between the active entry and the rail's own scroll edges. */
const FOLLOW_MARGIN = 8

export function ContentsRail({
  toc,
  slug,
  railFollows = true,
}: {
  toc: TocEntry[]
  /** The article this rail belongs to — carried on every section event so read
      depth is attributable to a piece, not just to "an article". */
  slug: string
  railFollows?: boolean
}) {
  const [on, setOn] = useState(toc[0]?.anchor ?? '')
  const [open, setOpen] = useState(false)
  const railRef = useRef<HTMLElement | null>(null)
  /* Sections already reported on THIS page view. Deliberately a ref and not
     gaFirstTime(): that guard is localStorage-backed, i.e. once per browser
     forever, which is right for a milestone and wrong for read depth — a
     returning reader would report nothing and look like they bounced. */
  const reported = useRef<Set<string>>(new Set())
  const pinned = useRef<string | null>(null)
  const ticking = useRef(false)
  const onRef = useRef(on)
  const openRef = useRef(open)

  onRef.current = on
  openRef.current = open

  /* anchor -> its level and its position among TOP-LEVEL sections. The ordinal
     counts h2s only, so `index` and `of` describe the same scale and a funnel
     built on them reads 1..N with no gaps where sub-headings were skipped. */
  const index = useMemo(() => {
    const m = new Map<string, { level: 2 | 3; ordinal: number }>()
    let ordinal = 0
    for (const t of toc) {
      if (t.level === 2) ordinal += 1
      m.set(t.anchor, { level: t.level, ordinal })
    }
    return m
  }, [toc])
  const topLevelCount = useMemo(() => toc.filter((t) => t.level === 2).length, [toc])

  useEffect(() => {
    const rail = railRef.current
    if (!rail || !toc.length) return

    const heads = () =>
      Array.from(
        document.querySelectorAll<HTMLElement>(
          '.lgx-article main h2[id], .lgx-article main h3[id]',
        ),
      )

    const follow = (id: string) => {
      const nav = rail.querySelector('nav')
      const a = nav?.querySelector<HTMLElement>(`a[href="#${CSS.escape(id)}"]`)
      if (!a) return
      const ar = a.getBoundingClientRect()
      const rr = rail.getBoundingClientRect()
      if (ar.top < rr.top + FOLLOW_MARGIN) {
        rail.scrollTop -= rr.top + FOLLOW_MARGIN - ar.top
      } else if (ar.bottom > rr.bottom - FOLLOW_MARGIN) {
        rail.scrollTop += ar.bottom - rr.bottom + FOLLOW_MARGIN
      }
    }

    const mark = () => {
      let id = pinned.current
      if (!id) {
        const hs = heads()
        if (!hs.length) return
        const y = window.scrollY + SPY_OFFSET
        let cur = hs[0]
        for (const h of hs) {
          if (h.offsetTop <= y) cur = h
          else break
        }
        id = cur.id
      }
      if (onRef.current === id) return
      onRef.current = id
      setOn(id)
      reportSection(id)
      if (railFollows) follow(id)
    }

    /* Read depth. The rail already knows which section the reader is in — that
       is what drives the spine — so this is that same signal, reported once per
       section per page view. Top-level sections only: the table of contents here
       runs to 42 entries once sub-headings are counted, and a reader crossing 42
       beacons is measuring nothing the 24 that name real sections do not. */
    const reportSection = (id: string) => {
      const entry = index.get(id)
      if (!entry || entry.level !== 2) return
      if (reported.current.has(id)) return
      reported.current.add(id)
      gaEvent('article_section', {
        article: slug,
        section: id,
        index: entry.ordinal,
        of: topLevelCount,
      })
    }

    const onScroll = () => {
      if (ticking.current) return
      ticking.current = true
      requestAnimationFrame(() => {
        try {
          mark()
        } finally {
          ticking.current = false
        }
      })
    }

    const onClick = (e: MouseEvent) => {
      const target = e.target as Element | null
      if (!target) return
      const inRail = rail.contains(target)
      if (window.matchMedia(COMPACT).matches) {
        // collapsed spine on touch: the first tap opens the titles instead of jumping
        if (inRail && !openRef.current && window.matchMedia('(pointer:coarse)').matches) {
          e.preventDefault()
          setOpen(true)
          return
        }
        if (!inRail) setOpen(false)
      }
      const a = target.closest?.('.rail nav a')
      if (a) {
        pinned.current = (a.getAttribute('href') ?? '').slice(1)
        mark()
        setOpen(false)
      }
    }

    // the pin survives the anchor jump's own programmatic scroll; only a reader gesture
    // releases it
    const unpin = () => {
      pinned.current = null
    }

    const hash = decodeURIComponent(window.location.hash.slice(1))
    if (hash && toc.some((t) => t.anchor === hash)) pinned.current = hash

    document.addEventListener('click', onClick, true)
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)
    window.addEventListener('load', onScroll)
    const gestures = ['wheel', 'touchmove', 'keydown'] as const
    gestures.forEach((ev) => window.addEventListener(ev, unpin, { passive: true }))

    // content height changes (fonts, details toggles) move every entry
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(onScroll) : null
    ro?.observe(document.documentElement)
    document.fonts?.ready.then(onScroll).catch(() => {})

    mark()
    if (pinned.current) follow(pinned.current)

    return () => {
      document.removeEventListener('click', onClick, true)
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
      window.removeEventListener('load', onScroll)
      gestures.forEach((ev) => window.removeEventListener(ev, unpin))
      ro?.disconnect()
    }
  }, [toc, railFollows, index, topLevelCount, slug])

  const activeIndex = toc.findIndex((t) => t.anchor === on)

  /* The compact spine is capped at 20 dashes (artboard _mark). Past that, every step-th
     entry stays and the rest are marked data-dim: hidden in the desktop list, collapsed to
     nothing in the spine, but present again in the expanded panel. --toc-rows is what the
     pitch divides the viewport by: the surviving count when thinned, otherwise the
     effective count with sub-entries at three-quarters of a row. */
  const n = toc.length
  const step = n > 20 ? Math.ceil(n / 20) : 1
  const dim = (i: number) => step > 1 && i % step !== 0
  const rows =
    n > 20 ? Math.ceil(n / step) : toc.reduce((acc, t) => acc + (t.level === 3 ? 0.75 : 1), 0)

  return (
    <aside
      className="rail"
      ref={railRef}
      data-open={open ? '1' : undefined}
      style={{ '--toc-rows': rows } as React.CSSProperties}
    >
      <details open>
        <summary>Contents</summary>
        <p className="label">Contents</p>
        <nav>
          {toc.map((t, i) => (
            <a
              key={t.anchor}
              href={`#${t.anchor}`}
              className={t.level === 3 ? 'sub' : undefined}
              data-on={t.anchor === on ? '1' : undefined}
              data-passed={activeIndex >= 0 && i <= activeIndex ? '1' : undefined}
              data-dim={dim(i) ? '1' : undefined}
            >
              <span>{t.text}</span>
            </a>
          ))}
        </nav>
      </details>
    </aside>
  )
}
